import {
  Component,
  ViewChild,
  AfterViewInit,
  inject,
  DestroyRef,
  input,
  computed,
  output
} from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { Config } from '@shared/configs/config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';
import moment, { Moment } from 'moment';
import { MatDatepicker } from '@angular/material/datepicker';

@Component({
  selector: 'datepicker-v2',
  templateUrl: './date-picker-v2.component.html',
  styleUrls: ['./date-picker-v2.component.scss']
})
export class DatePickerV2 implements AfterViewInit {
  title = input<string | null>(null);
  disabled = input<boolean>(false);
  form = input<UntypedFormGroup>();
  hasError = input<boolean>(false);
  clearable = input<boolean>(true);
  controlName = input<string>('start');
  minDate = input<moment.Moment | Date | string | null>(null);
  maxDate = input<moment.Moment | Date | string | null>(null);
  startAt = input<moment.Moment | Date | string | null>(null);
  disabledRanges = input<{ start: string; end: string }[]>([]);

  closed = output<void>();

  @ViewChild('datepicker') datepicker: MatDatepicker<moment.Moment> | null = null;

  dateControl = new UntypedFormControl();
  displayDateControl = new UntypedFormControl();

  private destroyRef = inject(DestroyRef);

  ngAfterViewInit() {
    const initial = this.form()?.get(this.controlName())?.value;
    this.setDateControls(initial);

    this.setDateControls(this.form().get(this.controlName()).value);

    this.dateControl.valueChanges
      .pipe(
        tap((date) => this.setDateControls(date)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.form()
      .get(this.controlName())
      ?.valueChanges.pipe(
        tap((date) => this.syncInternalControls(date)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private syncInternalControls(date: any): void {
    if (!date) {
      this.displayDateControl.reset(null, { emitEvent: false });
      this.dateControl.setValue(null, { emitEvent: false });
      return;
    }

    const mDate = moment.isMoment(date) ? date : moment(date);

    this.displayDateControl.setValue(mDate.format(Config.DATE_FORMAT_DOTS), { emitEvent: false });
    this.dateControl.setValue(mDate, { emitEvent: false });
  }

  private setDateControls(date: moment.Moment | string | null): void {
    if (!date) {
      this.displayDateControl.reset(null, { emitEvent: false });
      this.form().get(this.controlName())?.reset();
      return;
    }

    const mDate = moment.isMoment(date) ? date : moment(date);

    this.displayDateControl.setValue(mDate.format(Config.DATE_FORMAT_DOTS), { emitEvent: false });

    if (moment.isMoment(date)) {
      this.form().get(this.controlName())?.setValue(mDate.format(Config.DATE_SERVER));
      this.form().get(this.controlName())?.markAsDirty();
    } else {
      this.dateControl.setValue(date, { emitEvent: false });
    }
  }

  private normalizeDate(value: string | moment.Moment | Date | null): Date | null {
    if (!value) return null;
    if (moment.isMoment(value)) return value.toDate();
    if (typeof value === 'string') return moment(value).toDate();
    return value;
  }

  getMinDate(): Date | null {
    return this.normalizeDate(this.minDate());
  }

  getMaxDate(): Date | null {
    return this.normalizeDate(this.maxDate());
  }

  getStartAtDate(): Date | null {
    return this.normalizeDate(this.startAt());
  }

  resetValue(): void {
    this.dateControl.setValue(null);
  }

  private normalizedBlockedRanges = computed(() => {
    const ranges = this.disabledRanges() ?? [];

    return ranges
      .filter((range) => range && range.start && range.end)
      .map((range) => ({
        start: moment(range.start).valueOf(),
        end: moment(range.end).valueOf()
      }));
  });

  dateFilter = (d: Moment | null): boolean => {
    if (!d || !moment.isMoment(d)) return false;
    const time = d.valueOf();

    const blockedRanges = this.normalizedBlockedRanges();
    const isBlocked = blockedRanges.some((range) => time >= range.start && time <= range.end);

    return !isBlocked;
  };
}
